import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Plus } from "lucide-react";
import type { Property } from "@/types/property";
import {
  formatArea,
  formatBedrooms,
  formatListingTitle,
  formatPrice,
} from "@/lib/format";
import { SafeImage } from "./SafeImage";
import { cn } from "@/lib/utils";
import { useCompare } from "@/lib/compare-context";
import { getPropertyCoverOffsetMap, rankPropertyImages } from "@/lib/property-images";
import { propertyPath } from "@/lib/property-url";

interface PropertyTableProps {
  items: Property[];
  loading?: boolean;
}

/**
 * Inventory-style table view for /properties. Tight, scannable rows
 * with image / community / type / beds / baths / area / price /
 * listing badge. Horizontally scrollable on small screens.
 */
export function PropertyTable({ items, loading }: PropertyTableProps) {
  const compare = useCompare();
  const coverOffsets = useMemo(() => getPropertyCoverOffsetMap(items), [items]);
  return (
    <div className="card-flat overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[920px] w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-[var(--line-strong)] bg-[var(--surface)]/40">
              <Th className="w-[44px]"></Th>
              <Th className="w-[68px]"></Th>
              <Th>Property</Th>
              <Th className="w-[140px]">Type</Th>
              <Th className="w-[88px] text-right">Beds</Th>
              <Th className="w-[88px] text-right">Baths</Th>
              <Th className="w-[120px] text-right">Area</Th>
              <Th className="w-[160px] text-right">Price</Th>
              <Th className="w-[128px] text-right">Listing</Th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-[var(--line)]">
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j} className="px-3 py-3.5 first:pl-5 last:pr-5">
                      <div className="h-3 w-full animate-pulse bg-[var(--surface-2)]" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading &&
              items.map((p) => {
                const labelTag = p.isExclusive
                  ? "Exclusive"
                  : p.listingType === "off-plan"
                    ? "Off-plan"
                    : p.listingType === "rent"
                      ? "For rent"
                      : "For sale";
                const badgeClass = p.isExclusive ? "badge badge-gold" : "badge badge-navy";
                const isCompared = compare.has(p.id);
                const displayImages = rankPropertyImages(p.images, p.id, {
                  coverOffset: coverOffsets.get(p.id) ?? 0,
                });

                return (
                  <tr
                    key={p.id}
                    className="group border-b border-[var(--line)] transition-colors hover:bg-[var(--surface)]/60"
                  >
                    <Td>
                      <button
                        type="button"
                        aria-pressed={isCompared}
                        aria-label={isCompared ? "Remove from compare" : "Add to compare"}
                        onClick={() => {
                          const r = compare.toggle(p.id);
                          if (r === "limit") alert(`You can compare up to ${compare.max} homes at a time.`);
                        }}
                        className={cn(
                          "inline-flex size-6 items-center justify-center border transition-colors",
                          isCompared
                            ? "border-[var(--cream)] bg-[var(--cream)] text-[var(--navy)]"
                            : "border-[var(--line-strong)] text-[var(--text-muted)] hover:border-[var(--cream)] hover:text-[var(--cream)]",
                        )}
                      >
                        {isCompared ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
                      </button>
                    </Td>
                    <Td>
                      <Link to={propertyPath(p)} className="block">
                        <div className="relative aspect-[4/3] w-12 overflow-hidden bg-[var(--surface-2)]">
                          <SafeImage
                            src={displayImages}
                            alt={formatListingTitle(p)}
                            loading="lazy"
                            sizes="48px"
                            widths={[96, 144]}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      </Link>
                    </Td>
                    <Td>
                      <Link to={propertyPath(p)} className="block">
                        <p className="font-display font-semibold text-[14.5px] leading-tight tracking-[-0.02em] text-[var(--text)] transition-colors group-hover:text-[var(--cream)]">
                          {formatListingTitle(p)}
                        </p>
                        <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                          {p.community}
                          {p.subCommunity ? ` · ${p.subCommunity}` : ""}
                        </p>
                      </Link>
                    </Td>
                    <Td>
                      <span className="text-[13px] capitalize text-[var(--text-soft)]">
                        {p.propertyType}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">{formatBedrooms(p.bedrooms)}</Td>
                    <Td className="text-right tabular-nums">{p.bathrooms}</Td>
                    <Td className="text-right tabular-nums text-[var(--text-soft)]">
                      {formatArea(p.area)}
                    </Td>
                    <Td className="text-right tabular-nums">
                      <span className="font-display font-semibold text-[14.5px] tracking-[-0.02em] text-[var(--cream)]">
                        {formatPrice(p.price, p.pricePeriod)}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <span className={badgeClass}>{labelTag}</span>
                    </Td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "px-3 py-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)] first:pl-5 last:pr-5",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <td
      className={cn(
        "px-3 py-3 align-middle text-[var(--text)] first:pl-5 last:pr-5",
        className,
      )}
    >
      {children}
    </td>
  );
}
